import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import {
  calculateColor,
  formatDate,
  transformData,
} from './conjunctionCalendarComponentUtils';
import TableLoadingSpinner from '@/components/ui/table/TableLoadingSpinner';
import {
  useChartInteraction,
  useGetTimeValues,
  useDateNavigation,
} from './conjunctionCalendarComponentHooks';
import ConjunctionCalendarTooltip from '@/components/core/tooltip/ConjunctionCalendarTooltip';
import { isMobile } from 'react-device-detect';
import styled from 'styled-components';

const ConjunctionCalendarComponent = ({
  probabilityInfo,
  tableData,
  isLoading,
  isFetching,
}) => {
  const {
    currentDate,
    filteredData,
    goToPreviousDate,
    goToNextDate,
    hasPrevious,
    hasNext,
  } = useDateNavigation(isMobile ? tableData : null);

  const { startOfDay, endOfDay, tickValues, dayValues } = useGetTimeValues(
    isMobile ? currentDate : null
  );
  const { handleMouseMove, handleMouseLeave, handleNodeClick, getNodeSize } =
    useChartInteraction();

  const conjunctionData = transformData(isMobile ? filteredData : tableData);

  if (isLoading || isFetching)
    return <TableLoadingSpinner width={'30px'} height={'30px'} />;

  return (
    <Section>
      <ResponsiveScatterPlot
        data={conjunctionData}
        nodeSize={getNodeSize}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={!isMobile && handleNodeClick}
        colors={node => calculateColor(node.serieId, probabilityInfo)}
        tooltip={ConjunctionCalendarTooltip}
        margin={
          isMobile
            ? { top: 80, right: 10, bottom: 30, left: 36 }
            : { top: 110, right: 20, bottom: 60, left: 90 }
        }
        xScale={{
          type: 'time',
          format: '%Y-%m-%d %H:%M:%S.%L',
          precision: 'second',
          min: startOfDay,
          max: endOfDay,
        }}
        xFormat='time:%Y-%m-%d %H:%M:%S'
        axisTop={{
          format: '%H',
          tickValues: tickValues,
          tickSize: isMobile ? 3 : 6,
          legend: 'TCA (UTC)',
          legendPosition: 'middle',
          legendOffset: isMobile ? -30 : -95,
        }}
        yScale={{
          type: 'linear',
          min: 0,
          max: 'auto',
          reverse: true,
        }}
        yFormat='>-.3f'
        enableGridX={true}
        gridXValues={dayValues}
        enableGridY={true}
        gridYValues={[0]}
        axisLeft={{
          tickSize: isMobile ? 3 : 10,
          ...(isMobile
            ? {}
            : {
                legend: 'DCA (km)',
                legendPosition: 'middle',
                legendOffset: -80,
              }),
        }}
        blendMode='multiply'
        axisBottom={{
          tickSize: 0,
          format: '',
          ...(isMobile
            ? {
                legend: 'DCA (km)',
                legendPosition: 'start',
                legendOffset: 20,
              }
            : {}),
        }}
        axisRight={null}
      />
      <DateLabel $ismobile={isMobile}>
        {dayValues.map((day, index) => (
          <div key={index}>{formatDate(day)}</div>
        ))}
      </DateLabel>

      {isMobile && (
        <NavigationSection>
          <NavigationButton
            onClick={goToPreviousDate}
            disabled={!hasPrevious}
            $disabled={!hasPrevious}
          >
            ←
          </NavigationButton>
          <NavigationButton
            onClick={goToNextDate}
            disabled={!hasNext}
            $disabled={!hasNext}
          >
            →
          </NavigationButton>
        </NavigationSection>
      )}
    </Section>
  );
};

export default ConjunctionCalendarComponent;

const Section = styled.section`
  width: 100%;
  height: 100%;
  position: relative;
`;

const NavigationSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${isMobile ? '0.5rem' : '1rem'};
  padding: ${isMobile ? '0.25rem 0' : '0.5rem 0'};
  padding-left: ${isMobile ? '0' : '0'};
  padding-right: ${isMobile ? '10px' : '0'};
`;

const NavigationButton = styled.button<{ $disabled: boolean }>`
  background: ${props => (props.$disabled ? '#cccccc' : '#001326')};
  color: white;
  border: none;
  padding: ${isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem'};
  border-radius: 4px;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  font-size: ${isMobile ? '0.8rem' : '0.9rem'};
  font-weight: 500;
  transition: none;

  &:disabled {
    opacity: 0.5;
  }
`;

const DateLabel = styled.div<{
  $ismobile: boolean;
}>`
  position: absolute;
  top: ${({ $ismobile }) => ($ismobile ? '20px' : 'calc(100px - 3.5rem)')};
  left: ${({ $ismobile }) => ($ismobile ? '0' : '0')};
  right: ${({ $ismobile }) => ($ismobile ? '0' : '0')};

  box-sizing: border-box;
  height: ${({ $ismobile }) => ($ismobile ? '1.5rem' : '2rem')};
  padding-left: ${({ $ismobile }) => ($ismobile ? '30px' : '90px')};
  padding-right: ${({ $ismobile }) => ($ismobile ? '5px' : '20px')};

  display: flex;
  align-items: center;
  > :nth-child(n) {
    background-color: #001326;
    color: #ffffff;
    font-weight: 700;
    font-size: ${({ $ismobile }) => ($ismobile ? '1rem' : '1.3rem')};
    text-align: center;
    flex: 1;
  }
`;
